"use client";

import { Button } from "@/components/ui/button";
import { Marquee } from "@/components/ui/Marquee";
import { ArrowRight, Leaf, Sparkles, Zap } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import Link from "next/link";
export const logos = [
  {
    alt: "Hair salons",
    src: "/images/landing_page/types/1.jpg",
    href: "/solutions/hair-salons",
    category: "beauty",
  },
  {
    alt: "Barbershop",
    src: "/images/landing_page/types/2.jpg",
    href: "/solutions/barbershops",
    category: "beauty",
  },
  {
    alt: "Nail studios",
    src: "/images/landing_page/types/3.jpg",
    href: "/solutions/nail-studios",
    category: "beauty",
  },
  {
    alt: "Spa",
    src: "/images/landing_page/types/4.jpg",
    href: "/solutions/spas",
    category: "wellness",
  },
  {
    alt: "Medspas",
    src: "/images/landing_page/types/5.jpg",
    href: "/solutions/med-spas",
    category: "wellness",
  },
  {
    alt: "Tattoo & piercing",
    src: "/images/landing_page/types/7.jpg",
    href: "/solutions/tattoo-piercing",
    category: "specialized",
  },
  {
    alt: "Massage & bodywork",
    src: "/images/landing_page/types/6.jpg",
    href: "/solutions/massage",
    category: "wellness",
  },
  {
    alt: "Makeup artist",
    src: "/images/landing_page/types/8.jpg",
    href: "/solutions/makeup-artists",
    category: "beauty",
  },
  {
    alt: "Hair removal",
    src: "/images/landing_page/types/9.jpg",
    href: "/solutions/hair-removal",
    category: "specialized",
  },
  {
    alt: "Lash & Brow",
    src: "/images/landing_page/types/10.jpg",
    href: "/solutions/brow-lash",
    category: "beauty",
  },
  {
    alt: "Tanning center",
    src: "/images/landing_page/types/11.jpg",
    href: "/solutions/tanning",
    category: "specialized",
  },
];
export const industryCategories = [
  {
    key: "beauty",
    label: "Beauty",
    icon: Sparkles,
    gradient: "from-[#ff8a57] via-[#ff692d] to-[#e5541c]",
    glow: "shadow-[0_8px_30px_-6px_rgba(255,105,45,0.55)]",
  },
  {
    key: "wellness",
    label: "Wellness",
    icon: Leaf,
    gradient: "from-[#ff8a57] via-[#ff692d] to-[#e5541c]",
    glow: "shadow-[0_8px_30px_-6px_rgba(255,105,45,0.55)]",
  },
  {
    key: "specialized",
    label: "Specialized",
    icon: Zap,
    gradient: "from-[#ff8a57] via-[#ff692d] to-[#e5541c]",
    glow: "shadow-[0_8px_30px_-6px_rgba(255,105,45,0.55)]",
  },
];

const categoryLabels = Object.fromEntries(
  industryCategories.map((category) => [category.key, category.label]),
);

export function TypesStrip() {
  const [active, setActive] = useState("beauty");
  const marqueeRef = useRef(null);
  const glidingRef = useRef(false);
  const types = industryCategories.flatMap((category) =>
    logos.filter((type) => type.category === category.key),
  );

  const getAnimation = () =>
    marqueeRef.current?.querySelector(".marquee")?.getAnimations()[0];

  // Highlight the tab of whichever card is passing the center of the strip.
  useEffect(() => {
    const timer = setInterval(() => {
      const container = marqueeRef.current;
      if (!container || glidingRef.current) return;
      const box = container.getBoundingClientRect();
      if (box.bottom < 0 || box.top > window.innerHeight) return;
      const centerX = box.left + box.width / 2;
      for (const card of container.querySelectorAll("[data-category]")) {
        const rect = card.getBoundingClientRect();
        if (rect.left <= centerX && rect.right >= centerX) {
          setActive(card.dataset.category);
          return;
        }
      }
    }, 200);
    return () => clearInterval(timer);
  }, []);

  // Glide the marquee forward until the category's next card reaches the center.
  const selectCategory = (key) => {
    setActive(key);
    const container = marqueeRef.current;
    const animation = getAnimation();
    if (!container || !animation) return;

    const box = container.getBoundingClientRect();
    const centerX = box.left + box.width / 2;
    let distance = Infinity;
    for (const card of container.querySelectorAll(`[data-category="${key}"]`)) {
      const rect = card.getBoundingClientRect();
      const offset = rect.left + rect.width / 2 - centerX;
      if (offset >= 0 && offset < distance) distance = offset;
    }
    if (!Number.isFinite(distance)) return;

    const duration = animation.effect.getComputedTiming().duration;
    const track = container.querySelector(".marquee");
    // One loop moves the track by half its width plus half the gap.
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const travel = (track.offsetWidth + gap) / 2;
    const from = animation.currentTime ?? 0;
    const to = from + (distance / travel) * duration;
    const glideMs = 1200;
    let start = null;

    glidingRef.current = true;
    let done = false;
    // Fallback in case animation frames are paused (e.g. a background tab).
    setTimeout(() => {
      if (!done) animation.currentTime = to % duration;
      glidingRef.current = false;
    }, glideMs + 200);
    const step = (now) => {
      start ??= now;
      const t = Math.min((now - start) / glideMs, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      animation.currentTime = (from + (to - from) * eased) % duration;
      if (t < 1) requestAnimationFrame(step);
      else {
        done = true;
        glidingRef.current = false;
      }
    };
    requestAnimationFrame(step);
  };

  return (
    <section className="relative bg-neutral-900 pt-18 pb-18">
      <div className="absolute inset-0 bg-[radial-gradient(115%_115%_at_50%_100%,#171717_10%,transparent_100%)]"></div>

      <h2 className="relative mx-auto max-w-5xl px-4 text-center text-4xl leading-[98%] tracking-[-0.03em] text-white md:text-[4.5rem]">
        What&apos;s your{" "}
        <span className="relative inline-block">
          industry?
          <span className="absolute inset-x-0 -bottom-1.5 h-1 rounded-full bg-fe-primary"></span>
        </span>
      </h2>
      <div className="relative mt-16 flex justify-center px-4">
        <div
          role="tablist"
          aria-label="Industry categories"
          className="flex w-full max-w-xl gap-1.5 rounded-full border border-white/10 bg-gradient-to-b from-white/[0.09] to-white/[0.03] p-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_10px_40px_-10px_rgba(0,0,0,0.6)] backdrop-blur-xl"
        >
          {industryCategories.map((category) => {
            const isActive = category.key === active;
            const Icon = category.icon;
            return (
              <button
                type="button"
                role="tab"
                key={category.key}
                onClick={() => selectCategory(category.key)}
                aria-selected={isActive}
                className={`relative flex flex-1 items-center justify-center gap-2 overflow-hidden rounded-full px-3 py-3 text-sm font-semibold transition-all duration-300 md:text-base ${
                  isActive
                    ? `bg-gradient-to-br ${category.gradient} ${category.glow} text-white`
                    : "text-white/60 hover:bg-white/[0.06] hover:text-white"
                }`}
              >
                {isActive && (
                  <span className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-t-full bg-gradient-to-b from-white/35 to-transparent"></span>
                )}
                <Icon className="relative size-4 shrink-0" />
                <span className="relative">{category.label}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div ref={marqueeRef} className="relative mt-14 mb-38">
        <Marquee
          pauseOnHover={true}
          repeat={2}
          duration={"120s"}
          className="p-0"
        >
          {types.map((brand) => (
            <Link
              href={brand.href}
              className="group relative mx-6 max-w-[22rem] rounded-3xl"
              data-category={brand.category}
              key={brand.href}
            >
              <div className="max-h-[28rem] overflow-hidden rounded-3xl">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={brand.src}
                  alt={brand.alt}
                  className="h-[28rem] w-[22rem] object-cover transition-transform delay-100 duration-300 ease-in-out group-hover:scale-110"
                />
              </div>
              <div className="absolute inset-0 z-10 flex w-full flex-col justify-end rounded-3xl bg-gradient-to-t from-black from-[-5%] to-transparent to-30% bg-cover bg-center bg-no-repeat">
                <div className="flex flex-row items-end justify-between gap-2 px-5 pb-4 text-white lg:px-6">
                  <div className="min-w-0">
                    <p className="text-xs font-medium tracking-[0.08em] text-white/70 uppercase">
                      {categoryLabels[brand.category]}
                    </p>
                    <p className="mb-1 truncate text-2xl font-semibold">
                      {brand.alt}
                    </p>
                  </div>
                  <div className="mb-1 shrink-0 rounded-full border border-white/25 bg-white/15 p-2 backdrop-blur-md transition-colors duration-300 group-hover:border-white group-hover:bg-white">
                    <ArrowRight className="size-5 text-white transition-all duration-300 group-hover:-rotate-45 group-hover:text-neutral-900" />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </Marquee>
      </div>
      <div className="mx-auto max-w-5xl">
        <h2 className="relative mx-auto max-w-5xl px-4 text-center text-4xl leading-[101%] tracking-[-0.03em] text-white md:text-[4.5rem]">
          Built for every kind of beauty & bodywork
          <span className="relative inline-block ml-5">
            professional
            <span className="absolute inset-x-0 -bottom-1.5 h-1 rounded-full bg-fe-primary"></span>
          </span>
        </h2>
        <p className="relative mt-18 px-4 text-center text-lg leading-relaxed text-white">
          Simplify how businesses schedule, manage, and grow their network of
          expert service providers
        </p>
        <div className="mt-10 flex justify-center">
          <Button
            className="h-11 bg-white text-neutral-900"
            style={{ "--primary": "#ffffff 0" }}
            variant="gradient"
            asChild
          >
            <Link href="/signup">Try it for free</Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
